<template>
  <div class="about-page">
    <!-- ===== 顶部标题区 ===== -->
    <!-- <section class="about-hero">
      <div class="hero-bg">
        <div class="hero-orb hero-orb-1"></div>
        <div class="hero-orb hero-orb-2"></div>
        <div class="hero-grid"></div>
      </div>
      <div class="hero-content">
        <span class="section-badge">
          <Sparkles :size="14" />
          Aetrivol
        </span>
        <h1 class="hero-title">{{ $t("aboutPage.s1Title") }}</h1>
        <p class="hero-sub">{{ $t("aboutPage.toc2") }}</p>
        <p class="hero-desc">{{ $t("aboutPage.toc3") }}</p>
      </div>
    </section> -->

    <!-- ===== 主体：左侧标签（吸顶） + 右侧面板 ===== -->
    <section class="about-body">
      <div class="about-container">
        <!-- 左侧标签导航：点击切换右侧面板，不做锚点滚动 -->
        <aside class="about-sidebar">
          <div class="sidebar-sticky">
            <!-- <div class="sidebar-title">
              <span class="title-text">{{ $t("aboutPage.tocTitle") }}</span>
            </div> -->
            <nav
              class="sidebar-nav"
              role="tablist"
              aria-orientation="vertical"
              :aria-label="$t('aboutPage.tocTitle')"
              @keydown="onNavKeydown"
            >
              <a
                v-for="item in toc"
                :key="item.id"
                :href="`#${item.id}`"
                class="sidebar-link"
                :class="{ active: activeSection === item.id }"
                role="tab"
                :aria-selected="activeSection === item.id"
                :aria-controls="item.id"
                :tabindex="activeSection === item.id ? 0 : -1"
                @click.prevent="switchSection(item.id)"
              >
                <span class="link-icon" aria-hidden="true">
                  <component :is="item.icon" :size="17" :stroke-width="1.8" />
                </span>
                <span class="link-text">{{ item.label }}</span>
              </a>
            </nav>
          </div>
        </aside>

        <!-- 右侧内容 -->
        <main class="about-content">
          <!-- ============ 1. 关于我们 ============ -->
          <section
            v-show="activeSection === 'tab1'"
            id="tab1"
            class="about-section"
            role="tabpanel"
            :aria-label="$t('aboutPage.toc1')"
          >
            <h2 class="section-title-text">{{ $t("aboutPage.s1Title") }}</h2>
            <div class="section-content">
              <p class="lead-text">{{ $t("aboutPage.s1P1") }}</p>
              <p class="body-text">{{ $t("aboutPage.s1P2") }}</p>

              <ul class="bullet-list">
                <li v-for="item in painPoints" :key="item.key">
                  <span class="bullet-icon"><Check :size="14" /></span>
                  <span class="bullet-text">
                    <strong v-if="item.label" class="bullet-label">
                      {{ item.label }}
                    </strong>
                    <span>{{ item.body }}</span>
                  </span>
                </li>
              </ul>

              <p class="body-text">{{ $t("aboutPage.s1P3") }}</p>

              <!-- 平台数据概览 -->
              <div class="stat-grid">
                <div v-for="s in stats" :key="s.labelKey" class="stat-card">
                  <component :is="s.icon" :size="20" class="stat-icon" />
                  <div class="stat-value">{{ $t(s.valueKey) }}</div>
                  <div class="stat-label">{{ $t(s.labelKey) }}</div>
                </div>
              </div>

              <!-- 核心业务矩阵 -->
              <h3 class="sub-title">
                <Layers :size="18" class="sub-title-icon" />
                {{ $t("aboutPage.s1H2a") }}
              </h3>
              <div class="matrix-grid">
                <article
                  v-for="item in matrix"
                  :key="item.titleKey"
                  class="matrix-card"
                >
                  <div class="matrix-head">
                    <component :is="item.icon" :size="18" class="matrix-icon" />
                    <h4>{{ $t(item.titleKey) }}</h4>
                  </div>
                  <p class="matrix-desc">{{ $t(item.descKey) }}</p>
                  <ul class="bullet-list">
                    <li v-for="row in item.rows" :key="row.key">
                      <span class="bullet-icon"><Check :size="14" /></span>
                      <span class="bullet-text">
                        <strong v-if="row.label" class="bullet-label">
                          {{ row.label }}
                        </strong>
                        <span>{{ row.body }}</span>
                      </span>
                    </li>
                  </ul>
                </article>
              </div>

              <!-- 独特竞争优势 -->
              <h3 class="sub-title">
                <ShieldCheck :size="18" class="sub-title-icon" />
                {{ $t("aboutPage.s1H2b") }}
              </h3>
              <div class="advantage-list">
                <div
                  v-for="item in advantages"
                  :key="item.key"
                  class="advantage-item"
                >
                  <div class="advantage-icon">
                    <component :is="item.icon" :size="20" />
                  </div>
                  <div class="advantage-text">
                    <strong v-if="item.label" class="advantage-label">
                      {{ item.label }}
                    </strong>
                    <span>{{ item.body }}</span>
                  </div>
                </div>
              </div>

              <!-- 结语 -->
              <h3 class="sub-title">
                <Rocket :size="18" class="sub-title-icon" />
                {{ $t("aboutPage.s1H2c") }}
              </h3>
              <blockquote class="closing-quote">
                {{ $t("aboutPage.s1P6") }}
              </blockquote>
            </div>
          </section>

          <!-- ============ 2. 核心人员组织 ============ -->
          <section
            v-show="activeSection === 'tab2'"
            id="tab2"
            class="about-section"
            role="tabpanel"
            :aria-label="$t('aboutPage.toc2')"
          >
            <h2 class="section-title-text">{{ $t("aboutPage.s2Title") }}</h2>
            <div class="section-content">
              <div
                v-for="(group, gi) in teamGroups"
                :key="group.titleKey"
                class="team-group"
                :class="group.layout"
              >
                <h3 class="group-title">
                  <span class="group-index">{{ gi + 1 }}</span>
                  <Users :size="18" class="group-icon" />
                  {{ $t(group.titleKey) }}
                </h3>
                <div class="member-cards" :class="group.layout">
                  <article
                    v-for="member in group.members"
                    :key="member.name"
                    class="member-card"
                    :class="group.layout"
                  >
                    <div class="member-photo-wrap">
                      <img
                        class="member-photo"
                        :src="member.photo"
                        :alt="member.name"
                      />
                    </div>
                    <div class="member-info">
                      <h4 class="member-name">{{ member.name }}</h4>
                      <div v-if="member.roleKey" class="member-role">
                        {{ $t(member.roleKey) }}
                      </div>
                      <p class="member-bio">
                        <span class="bio-label">
                          {{ $t("aboutPage.s2BioLabel") }}
                        </span>
                        {{ $t(member.bioKey) }}
                      </p>
                    </div>
                  </article>
                </div>
              </div>
            </div>
          </section>

          <!-- ============ 3. 全球地址 ============ -->
          <section
            v-show="activeSection === 'tab3'"
            id="tab3"
            class="about-section"
            role="tabpanel"
            :aria-label="$t('aboutPage.toc3')"
          >
            <h2 class="section-title-text">{{ $t("aboutPage.s3Title") }}</h2>
            <div class="section-content">
              <div class="map-card">
                <div class="map-head">
                  <div class="map-title">
                    <MapPin :size="18" />
                    {{ $t("aboutPage.s3MapTitle") }}
                  </div>
                  <div class="map-hint">
                    <Navigation :size="13" />
                    {{ $t("aboutPage.s3MapHint") }}
                  </div>
                </div>

                <!-- 世界地图（示意）：点击/悬停定位标记可切换地址 -->
                <div class="map-stage">
                  <svg
                    class="world-map"
                    viewBox="0 0 1000 480"
                    preserveAspectRatio="xMidYMid meet"
                    role="img"
                    :aria-label="$t('aboutPage.s3MapTitle')"
                  >
                    <defs>
                      <linearGradient
                        id="aboutLandFill"
                        x1="0"
                        y1="0"
                        x2="1"
                        y2="1"
                      >
                        <stop offset="0%" stop-color="#0e2b47" />
                        <stop offset="100%" stop-color="#1b1f45" />
                      </linearGradient>
                    </defs>

                    <!-- 经纬网格 -->
                    <g class="grid-layer">
                      <line
                        v-for="x in gridX"
                        :key="`gx-${x}`"
                        :x1="x"
                        y1="0"
                        :x2="x"
                        y2="480"
                      />
                      <line
                        v-for="y in gridY"
                        :key="`gy-${y}`"
                        x1="0"
                        :y1="y"
                        x2="1000"
                        :y2="y"
                      />
                    </g>

                    <!-- 陆地轮廓（示意简化） -->
                    <g class="land-layer">
                      <path
                        class="land"
                        d="M42,72 L60,50 L130,40 L210,38 L280,46 L310,62 L347,107 L320,112 L312,118 L300,130 L292,141 L284,152 L277,166 L268,172 L258,180 L252,190 L270,200 L244,196 L228,180 L208,177 L194,171 L168,148 L158,105 L140,96 L125,84 L100,62 Z"
                      />
                      <path
                        class="land"
                        d="M376,50 L440,42 L452,70 L415,88 L380,78 Z"
                      />
                      <path
                        class="land"
                        d="M286,210 L306,199 L333,204 L356,217 L403,245 L394,263 L389,289 L353,317 L328,332 L311,370 L297,365 L303,348 L300,307 L306,276 L275,240 L281,230 Z"
                      />
                      <path
                        class="land"
                        d="M474,131 L484,138 L506,124 L520,118 L540,128 L562,132 L580,124 L583,112 L604,88 L584,77 L569,49 L540,60 L514,72 L528,89 L505,100 L487,106 Z"
                      />
                      <path
                        class="land"
                        d="M483,138 L528,135 L556,148 L589,151 L603,174 L619,199 L642,202 L633,222 L611,238 L611,271 L597,291 L578,314 L550,317 L533,276 L525,233 L508,215 L478,217 L453,192 L456,174 L472,151 Z"
                      />
                      <path
                        class="land"
                        d="M587,152 L598,143 L603,133 L617,125 L639,102 L611,61 L667,51 L806,36 L944,46 L972,61 L931,89 L866,120 L853,134 L837,150 L806,179 L794,176 L794,207 L788,226 L779,195 L767,187 L751,169 L745,172 L723,197 L715,210 L712,204 L703,181 L700,174 L686,166 L668,163 L656,161 L662,170 L668,180 L650,186 L619,199 L608,175 L597,156 Z"
                      />
                      <path
                        class="land"
                        d="M817,286 L863,262 L905,273 L925,300 L920,317 L903,327 L885,319 L822,312 Z"
                      />
                      <path
                        class="land"
                        d="M496,94 L505,88 L509,100 L500,104 Z"
                      />
                      <path
                        class="land"
                        d="M866,143 L892,116 L898,124 L874,148 Z"
                      />
                      <path
                        class="land"
                        d="M770,250 L810,240 L840,246 L860,256 L820,262 L786,256 Z"
                      />
                      <path
                        class="land"
                        d="M820,212 L834,208 L838,222 L824,226 Z"
                      />
                      <path
                        class="land"
                        d="M630,300 L642,292 L646,314 L632,318 Z"
                      />
                      <path
                        class="land"
                        d="M938,352 L960,340 L966,360 L944,368 Z"
                      />
                      <path
                        class="land"
                        d="M40,436 L140,428 L300,424 L520,426 L700,424 L860,428 L960,436 L960,452 L40,452 Z"
                      />
                    </g>

                    <!-- 办公节点定位标记：悬停就地弹出地址气泡，点击固定到底部地址条 -->
                    <g class="pin-layer">
                      <g
                        v-for="(office, i) in offices"
                        :key="office.countryKey"
                        class="pin"
                        :class="{ active: activeOffice === i }"
                        :transform="`translate(${office.x}, ${office.y})`"
                        tabindex="0"
                        role="button"
                        @click="selectOffice(i)"
                        @mouseenter="hoverOffice = i"
                        @mouseleave="hoverOffice = null"
                        @focus="hoverOffice = i"
                        @blur="hoverOffice = null"
                        @keydown.enter.prevent="selectOffice(i)"
                        @keydown.space.prevent="selectOffice(i)"
                      >
                        <circle class="pin-ring" r="16" />
                        <circle class="pin-halo" r="10" />
                        <circle class="pin-dot" r="5.5" />
                        <text
                          class="pin-label"
                          :text-anchor="office.anchor"
                          :x="office.anchor === 'end' ? -14 : 14"
                          y="5"
                        >
                          {{ $t(office.countryKey) }}
                        </text>
                      </g>
                    </g>
                  </svg>

                  <!-- 悬停定位标记时就地弹出的地址气泡 -->
                  <div
                    v-if="hoveredOffice"
                    class="map-tip"
                    :class="`tip-${tipSide}`"
                    :style="tipPos.style"
                    role="tooltip"
                  >
                    <span
                      class="tip-arrow"
                      :style="{ left: tipPos.arrowLeft }"
                    />
                    <span class="tip-index">{{ hoverOffice + 1 }}</span>
                    <span class="tip-body">
                      <span class="tip-country">
                        {{ $t(hoveredOffice.countryKey) }}
                      </span>
                      <span class="tip-addr">
                        {{ $t(hoveredOffice.addrKey) }}
                      </span>
                    </span>
                  </div>
                </div>

                <!-- 当前选中的地址 -->
                <!-- <div class="map-active">
                  <span class="active-index">{{ activeOffice + 1 }}</span>
                  <span class="active-country">
                    {{ $t(activeOfficeData.countryKey) }}
                  </span>
                  <span class="active-addr">
                    {{ $t(activeOfficeData.addrKey) }}
                  </span>
                </div> -->
              </div>
            </div>
          </section>
          <!-- ============ 4. 资金安全 ============ -->
          <section
            v-show="activeSection === 'tab4'"
            id="tab4"
            class="about-section"
            role="tabpanel"
            :aria-label="$t('fundSafety.toc')"
          >
            <h2 class="section-title-text">{{ $t("fundSafety.title") }}</h2>
            <div class="section-content">
              <p class="lead-text">{{ $t("fundSafety.intro") }}</p>

              <!-- 每个主题：小标题 → 导语 → 托管背书（可选）→ 图示（可选）→ 细分条目 → 补充说明（可选） -->
              <div
                v-for="block in fundBlocks"
                :key="block.key"
                class="fund-topic"
              >
                <h3 class="sub-title">{{ $t(block.titleKey) }}</h3>

                <p class="body-text">{{ $t(block.introKey) }}</p>

                <div v-if="block.custody" class="fund-custody">
                  <h4 class="fund-custody-title">
                    {{ $t(block.custody.titleKey) }}
                  </h4>
                  <p class="fund-custody-intro">
                    {{ $t(block.custody.introKey) }}
                  </p>
                  <ul class="bullet-list">
                    <li
                      v-for="row in buildRows(block.custody.pointKeys)"
                      :key="row.key"
                    >
                      <span class="bullet-icon"><Check :size="14" /></span>
                      <span class="bullet-text">
                        <strong v-if="row.label" class="bullet-label">
                          {{ row.label }}
                        </strong>
                        <span>{{ row.body }}</span>
                      </span>
                    </li>
                  </ul>
                </div>

                <div v-if="block.diagram" class="fund-diagram">
                  <component :is="block.diagram" />
                </div>

                <ul v-if="block.pointKeys" class="bullet-list">
                  <li v-for="row in buildRows(block.pointKeys)" :key="row.key">
                    <span class="bullet-icon"><Check :size="14" /></span>
                    <span class="bullet-text">
                      <strong v-if="row.label" class="bullet-label">
                        {{ row.label }}
                      </strong>
                      <span>{{ row.body }}</span>
                    </span>
                  </li>
                </ul>

                <div v-if="block.detail" class="fund-detail-list">
                  <article
                    v-for="item in block.detail"
                    :key="item.titleKey"
                    class="fund-detail-card"
                  >
                    <h4 class="fund-detail-title">{{ $t(item.titleKey) }}</h4>
                    <p class="fund-detail-text">{{ $t(item.textKey) }}</p>
                  </article>
                </div>
              </div>
            </div>
          </section>
        </main>
      </div>
    </section>
  </div>
</template>

<script>
import {
  Activity,
  BookOpen,
  Building2,
  Check,
  Cpu,
  Layers,
  MapPin,
  MapPinned,
  Navigation,
  Rocket,
  Server,
  ShieldCheck,
  Sparkles,
  Target,
  TrendingUp,
  Users,
  UsersRound,
  Zap,
} from "lucide-vue";

/* 资金安全图示：纯 HTML/CSS 绘制，文案随语言切换 */
import FundDepositFlow from "@/components/fund/DepositFlow";
import FundPathCompare from "@/components/fund/PathCompare";
import FundProtectionSystem from "@/components/fund/ProtectionSystem";

/* 核心人员组织：分组 + 成员（姓名/照片为专有名词，无需翻译） */
const TEAM_GROUPS = [
  {
    titleKey: "aboutPage.s2Group1",
    layout: "grid",
    members: [
      {
        name: "Sam Altman",
        photo: require("@/assets/team/sam-altman.jpg"),
        roleKey: "aboutPage.s2Role1",
        bioKey: "aboutPage.s2Bio1",
      },
      {
        name: "Satya Nadella",
        photo: require("@/assets/team/satya-nadella.jpg"),
        roleKey: "aboutPage.s2Role2",
        bioKey: "aboutPage.s2Bio2",
      },
      {
        name: "Geoffrey Hinton",
        photo: require("@/assets/team/geoffrey-hinton.png"),
        roleKey: "aboutPage.s2Role3",
        bioKey: "aboutPage.s2Bio3",
      },
    ],
  },
  {
    titleKey: "aboutPage.s2Group2",
    layout: "wide",
    members: [
      {
        name: "Demis Hassabis",
        photo: require("@/assets/team/demis-hassabis.jpg"),
        roleKey: "",
        bioKey: "aboutPage.s2Bio4",
      },
    ],
  },
  {
    titleKey: "aboutPage.s2Group3",
    layout: "wide",
    members: [
      {
        name: "Mira Murati",
        photo: require("@/assets/team/mira-murati.jpg"),
        roleKey: "",
        bioKey: "aboutPage.s2Bio5",
      },
    ],
  },
  {
    titleKey: "aboutPage.s2Group4",
    layout: "wide",
    members: [
      {
        name: "Ilya Sutskever",
        photo: require("@/assets/team/ilya-sutskever.jpg"),
        roleKey: "",
        bioKey: "aboutPage.s2Bio6",
      },
    ],
  },
  {
    titleKey: "aboutPage.s2Group5",
    /* 部门负责人层级略低，使用更轻量的卡片样式 */
    layout: "compact",
    members: [
      {
        name: "Jony Ive",
        photo: require("@/assets/team/jony-ive.jpg"),
        roleKey: "aboutPage.s2Role7",
        bioKey: "aboutPage.s2Bio7",
      },
      {
        name: "Yann LeCun",
        photo: require("@/assets/team/yann-lecun.jpg"),
        roleKey: "aboutPage.s2Role8",
        bioKey: "aboutPage.s2Bio8",
      },
      {
        name: "Mustafa Suleyman",
        photo: require("@/assets/team/mustafa-suleyman.jpg"),
        roleKey: "aboutPage.s2Role9",
        bioKey: "aboutPage.s2Bio9",
      },
    ],
  },
];

/* 全球办公节点：坐标为示意图 viewBox(1000×480) 内的位置 */
const OFFICES = [
  {
    countryKey: "aboutPage.s3Country1",
    addrKey: "aboutPage.s3Addr1",
    x: 176,
    y: 138,
    anchor: "end",
  },
  {
    countryKey: "aboutPage.s3Country2",
    addrKey: "aboutPage.s3Addr2",
    x: 788,
    y: 222,
    anchor: "start",
  },
  {
    countryKey: "aboutPage.s3Country3",
    addrKey: "aboutPage.s3Addr3",
    x: 645,
    y: 162,
    anchor: "start",
  },
  {
    countryKey: "aboutPage.s3Country4",
    addrKey: "aboutPage.s3Addr4",
    x: 506,
    y: 104,
    anchor: "end",
  },
];

/* 地图示意图 viewBox 尺寸：用于把定位点坐标换算为容器百分比 */
const MAP_WIDTH = 1000;
const MAP_HEIGHT = 480;
/* 气泡锚点：translate 与箭头 left 取同一比例，保证箭头始终指向定位点 */
const TIP_SHIFT = { center: "-50%", left: "-8%", right: "-92%" };
const TIP_ARROW = { center: "50%", left: "8%", right: "92%" };
const TIP_GAP = "22px";

export default {
  name: "AboutPage",
  components: {
    Activity,
    BookOpen,
    Building2,
    Check,
    Cpu,
    FundDepositFlow,
    FundPathCompare,
    FundProtectionSystem,
    Layers,
    MapPin,
    MapPinned,
    Navigation,
    Rocket,
    Server,
    ShieldCheck,
    Sparkles,
    Target,
    TrendingUp,
    Users,
    UsersRound,
    Zap,
  },
  data() {
    return {
      activeSection: "tab1",
      activeOffice: 0,
      hoverOffice: null,
      teamGroups: TEAM_GROUPS,
      offices: OFFICES,
      stats: [
        {
          icon: "TrendingUp",
          labelKey: "aboutPage.s1Stat1Label",
          valueKey: "aboutPage.s1Stat1Value",
        },
        {
          icon: "Server",
          labelKey: "aboutPage.s1Stat2Label",
          valueKey: "aboutPage.s1Stat2Value",
        },
        {
          icon: "Activity",
          labelKey: "aboutPage.s1Stat3Label",
          valueKey: "aboutPage.s1Stat3Value",
        },
      ],
      gridX: [125, 250, 375, 500, 625, 750, 875],
      gridY: [80, 160, 240, 320, 400],
    };
  },
  computed: {
    // 目录（随语言切换实时更新；icon 为左侧菜单图标）
    toc() {
      return [
        { id: "tab1", icon: "Building2", label: this.$t("aboutPage.toc1") },
        { id: "tab2", icon: "UsersRound", label: this.$t("aboutPage.toc2") },
        { id: "tab4", icon: "ShieldCheck", label: this.$t("fundSafety.toc") },
        { id: "tab3", icon: "MapPinned", label: this.$t("aboutPage.toc3") },
      ];
    },
    // 资金安全：按官方公告组织（小标题 → 导语 → 托管背书 → 图示 → 细分条目 → 补充说明）
    fundBlocks() {
      return [
        {
          key: "digital-currency",
          diagram: "FundPathCompare",
          titleKey: "fundSafety.s1Title",
          introKey: "fundSafety.s1Intro",
          pointKeys: ["fundSafety.s1L1", "fundSafety.s1L2"],
        },
        {
          key: "protection",
          diagram: "FundProtectionSystem",
          titleKey: "fundSafety.s2Title",
          introKey: "fundSafety.s2Intro",
          custody: {
            titleKey: "fundSafety.s2CustodyTitle",
            introKey: "fundSafety.s2CustodyIntro",
            pointKeys: [
              "fundSafety.s2CustodyL1",
              "fundSafety.s2CustodyL2",
              "fundSafety.s2CustodyL3",
            ],
          },
          detail: [
            { titleKey: "fundSafety.s2H1", textKey: "fundSafety.s2P1" },
            { titleKey: "fundSafety.s2H2", textKey: "fundSafety.s2P2" },
            { titleKey: "fundSafety.s2H3", textKey: "fundSafety.s2P3" },
          ],
        },
        {
          key: "deposit-guide",
          diagram: "FundDepositFlow",
          titleKey: "fundSafety.s3Title",
          introKey: "fundSafety.s3Intro",
          pointKeys: [
            "fundSafety.s3L1",
            "fundSafety.s3L2",
            "fundSafety.s3L3",
          ],
        },
      ];
    },
    // 行业痛点
    painPoints() {
      return this.buildRows([
        "aboutPage.s1L1",
        "aboutPage.s1L2",
        "aboutPage.s1L3",
      ]);
    },
    // 核心业务矩阵
    matrix() {
      return [
        {
          icon: "Cpu",
          titleKey: "aboutPage.s1H3a",
          descKey: "aboutPage.s1P4",
          rows: this.buildRows(["aboutPage.s1L4", "aboutPage.s1L5"]),
        },
        {
          icon: "Server",
          titleKey: "aboutPage.s1H3b",
          descKey: "aboutPage.s1P5",
          rows: this.buildRows(["aboutPage.s1L6", "aboutPage.s1L7"]),
        },
      ];
    },
    // 独特竞争优势
    advantages() {
      return [
        { key: "aboutPage.s1L8", icon: "Target" },
        { key: "aboutPage.s1L9", icon: "ShieldCheck" },
        { key: "aboutPage.s1L10", icon: "Zap" },
      ].map((item) => Object.assign({}, item, this.buildRow(item.key)));
    },
    activeOfficeData() {
      return this.offices[this.activeOffice] || this.offices[0];
    },
    /* 鼠标悬停 / 键盘聚焦中的办公节点（无悬停时为 null） */
    hoveredOffice() {
      if (this.hoverOffice === null) return null;
      return this.offices[this.hoverOffice] || null;
    },
    /* 气泡朝上还是朝下：定位点在地图上半部时朝下弹，避免超出地图被裁切 */
    tipSide() {
      const office = this.hoveredOffice;
      if (!office) return "top";
      return office.y / MAP_HEIGHT < 0.5 ? "bottom" : "top";
    },
    /* 气泡位置：定位点坐标 → 容器百分比；靠边时切换锚点，避免气泡溢出 */
    tipPos() {
      const office = this.hoveredOffice;
      if (!office) return { style: {}, arrowLeft: TIP_ARROW.center };
      const xPct = (office.x / MAP_WIDTH) * 100;
      const anchor = xPct < 26 ? "left" : xPct > 74 ? "right" : "center";
      const gap =
        this.tipSide === "bottom" ? TIP_GAP : `calc(-100% - ${TIP_GAP})`;
      return {
        style: {
          left: `${xPct}%`,
          top: `${(office.y / MAP_HEIGHT) * 100}%`,
          transform: `translate(${TIP_SHIFT[anchor]}, ${gap})`,
        },
        arrowLeft: TIP_ARROW[anchor],
      };
    },
  },
  methods: {
    /* 「小标题：正文」拆分为加粗小标题 + 正文；无冒号或不像小标题时整体作为正文 */
    buildRow(key) {
      const text = this.$t(key);
      const matched = /^([^：:]{1,64})[：:]\s*(.+)$/.exec(text);
      if (!matched) return { key, label: "", body: text };
      const label = matched[1].trim();
      if (!label || label.length >= matched[2].length) {
        return { key, label: "", body: text };
      }
      return { key, label, body: matched[2] };
    },
    buildRows(keys) {
      return keys.map((key) => this.buildRow(key));
    },
    /* 点击 / 回车选中定位标记：把地址固定到地图下方地址条 */
    selectOffice(index) {
      this.activeOffice = index;
    },
    /* 切换右侧板块：仅显隐面板，不做锚点滚动，左栏 sticky 保持原位 */
    switchSection(id) {
      if (this.activeSection === id) return;
      this.activeSection = id;
      // 面板被隐藏时不会触发 mouseleave，这里主动清掉气泡状态
      this.hoverOffice = null;
    },
    /* 垂直 tablist 键盘导航：↑/↓ 前后切换，Home/End 跳到首尾 */
    onNavKeydown(event) {
      const keys = ["ArrowDown", "ArrowUp", "Home", "End"];
      if (keys.indexOf(event.key) === -1) return;
      event.preventDefault();
      const total = this.toc.length;
      const current = this.toc.findIndex(
        (item) => item.id === this.activeSection,
      );
      let next = current;
      if (event.key === "ArrowDown") next = (current + 1) % total;
      else if (event.key === "ArrowUp") next = (current - 1 + total) % total;
      else if (event.key === "Home") next = 0;
      else next = total - 1;
      this.switchSection(this.toc[next].id);
      // 焦点跟随到新的标签
      const links = event.currentTarget.querySelectorAll(".sidebar-link");
      if (links[next]) links[next].focus();
    },
  },
};
</script>

<style scoped>
/* ==================== 页面基础 ==================== */
.about-page {
  background: #0a0e1a;
  color: #fff;
  min-height: 100vh;
  /* 使用 clip 代替 hidden，避免生成滚动容器破坏左侧 position: sticky 吸顶 */
  overflow-x: clip;
}

/* ==================== 顶部标题区 ==================== */
.about-hero {
  position: relative;
  overflow: hidden;
  padding: 76px 20px 58px;
  text-align: center;
  background: linear-gradient(135deg, #080c18 0%, #0f1a2e 60%, #0b1424 100%);
}

.hero-bg {
  position: absolute;
  inset: 0;
  pointer-events: none;
}

.hero-orb {
  position: absolute;
  border-radius: 50%;
  filter: blur(120px);
  opacity: 0.16;
}

.hero-orb-1 {
  top: -260px;
  left: -120px;
  width: 520px;
  height: 520px;
  background: #00b4ff;
}

.hero-orb-2 {
  right: -100px;
  bottom: -220px;
  width: 420px;
  height: 420px;
  background: #7c3aed;
}

.hero-grid {
  position: absolute;
  inset: 0;
  background-image: linear-gradient(
      rgba(0, 180, 255, 0.05) 1px,
      transparent 1px
    ),
    linear-gradient(90deg, rgba(0, 180, 255, 0.05) 1px, transparent 1px);
  background-size: 48px 48px;
  -webkit-mask-image: radial-gradient(
    circle at 50% 40%,
    #000 15%,
    transparent 72%
  );
  mask-image: radial-gradient(circle at 50% 40%, #000 15%, transparent 72%);
}

.hero-content {
  position: relative;
  z-index: 1;
  max-width: 900px;
  margin: 0 auto;
}

.section-badge {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 16px;
  margin-bottom: 14px;
  border-radius: 20px;
  border: 1px solid rgba(0, 180, 255, 0.14);
  background: rgba(0, 180, 255, 0.08);
  color: #00b4ff;
  font-size: 13px;
  letter-spacing: 1px;
}

.hero-title {
  margin: 6px 0 12px;
  font-size: 46px;
  font-weight: 900;
  line-height: 1.2;
  letter-spacing: 1px;
  background: linear-gradient(135deg, #ffffff 30%, #00b4ff 100%);
  -webkit-background-clip: text;
  background-clip: text;
  -webkit-text-fill-color: transparent;
}

.hero-sub {
  margin: 0 0 6px;
  font-size: 16px;
  color: rgba(255, 255, 255, 0.62);
}

.hero-desc {
  margin: 0;
  font-size: 14px;
  color: rgba(255, 255, 255, 0.34);
}

/* ==================== 主体布局 ==================== */
.about-body {
  padding: 40px 20px 80px;
}

.about-container {
  display: flex;
  gap: 48px;
  align-items: flex-start;
  max-width: 1280px;
  margin: 0 auto;
}

.about-sidebar {
  position: sticky;
  top: 75px;
  flex: 0 0 258px;
  align-self: flex-start;
  max-height: calc(100vh - 100px);
  overflow-y: auto;
  scrollbar-width: none;
}

.about-sidebar::-webkit-scrollbar {
  display: none;
}

.sidebar-sticky {
  position: relative;
  padding: 20px 16px 16px;
  border-radius: 18px;
  border: 1px solid rgba(255, 255, 255, 0.07);
  background: linear-gradient(
    180deg,
    rgba(255, 255, 255, 0.055) 0%,
    rgba(255, 255, 255, 0.02) 100%
  );
  box-shadow: 0 22px 45px -32px rgba(0, 0, 0, 0.95);
  backdrop-filter: blur(10px);
}

/* 卡片顶部的高光细线 */
.sidebar-sticky::before {
  content: "";
  position: absolute;
  top: 0;
  left: 26px;
  right: 26px;
  height: 1px;
  background: linear-gradient(
    90deg,
    transparent,
    rgba(0, 180, 255, 0.6),
    transparent
  );
}

.sidebar-title {
  position: relative;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 0 0 14px 12px;
  margin-bottom: 14px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.06);
  font-size: 13px;
  font-weight: 700;
  letter-spacing: 0.06em;
  color: rgba(255, 255, 255, 0.84);
}

/* 标题左侧渐变竖条 */
.sidebar-title::before {
  content: "";
  position: absolute;
  left: 0;
  top: 2px;
  width: 3px;
  height: 14px;
  border-radius: 3px;
  background: linear-gradient(180deg, #00b4ff, #7c3aed);
}

.title-text {
  flex: 1 1 auto;
  min-width: 0;
}

.sidebar-nav {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.sidebar-link {
  position: relative;
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 11px 12px 11px 14px;
  border-radius: 12px;
  border: 1px solid transparent;
  font-size: 14px;
  font-weight: 500;
  line-height: 1.4;
  color: rgba(255, 255, 255, 0.5);
  text-decoration: none;
  transition: color 0.25s, background 0.25s, border-color 0.25s, transform 0.25s;
}

/* 左侧渐变竖条：仅当前面板显示 */
.sidebar-link::before {
  content: "";
  position: absolute;
  left: 0;
  top: 50%;
  width: 3px;
  height: 0;
  border-radius: 0 3px 3px 0;
  background: linear-gradient(180deg, #00b4ff, #7c3aed);
  transform: translateY(-50%);
  transition: height 0.25s;
}

.sidebar-link:hover {
  color: #fff;
  background: rgba(255, 255, 255, 0.05);
  transform: translateX(2px);
}

.sidebar-link.active {
  color: #fff;
  border-color: rgba(0, 180, 255, 0.22);
  background: linear-gradient(
    90deg,
    rgba(0, 180, 255, 0.18) 0%,
    rgba(124, 58, 237, 0.1) 100%
  );
  box-shadow: 0 14px 28px -22px rgba(0, 180, 255, 0.9);
}

.sidebar-link.active::before {
  height: 60%;
}

/* 菜单图标：胶囊底 + 激活时渐变高亮 */
.link-icon {
  position: relative;
  flex: 0 0 32px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 10px;
  border: 1px solid rgba(255, 255, 255, 0.08);
  background: rgba(255, 255, 255, 0.04);
  color: rgba(255, 255, 255, 0.58);
  transition: color 0.25s, background 0.25s, border-color 0.25s,
    box-shadow 0.25s, transform 0.25s;
}

.sidebar-link:hover .link-icon {
  color: #fff;
  border-color: rgba(255, 255, 255, 0.18);
  background: rgba(255, 255, 255, 0.09);
  transform: translateY(-1px);
}

.sidebar-link.active .link-icon {
  border-color: rgba(0, 180, 255, 0.45);
  background: linear-gradient(135deg, #00b4ff 0%, #7c3aed 100%);
  color: #fff;
  box-shadow: 0 10px 20px -12px rgba(0, 180, 255, 0.95),
    inset 0 1px 0 rgba(255, 255, 255, 0.32);
}

.link-text {
  flex: 1 1 auto;
  min-width: 0;
}

.about-content {
  flex: 1 1 auto;
  min-width: 0;
}

/* 面板切换：display 从 none 恢复时动画会自动重播 */
.about-section {
  margin-bottom: 56px;
  animation: panelIn 0.28s ease-out;
}

@keyframes panelIn {
  from {
    opacity: 0;
    transform: translateY(10px);
  }

  to {
    opacity: 1;
    transform: none;
  }
}

.section-title-text {
  position: relative;
  margin: 0 0 22px;
  padding-bottom: 14px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.06);
  font-size: 30px;
  font-weight: 800;
}

.section-title-text::after {
  content: "";
  position: absolute;
  left: 0;
  bottom: -1px;
  width: 64px;
  height: 2px;
  background: linear-gradient(90deg, #00b4ff, #7c3aed);
}

/* ==================== 1. 关于我们 ==================== */
.lead-text {
  margin: 0 0 16px;
  padding: 18px 20px;
  border-radius: 14px;
  border: 1px solid rgba(0, 180, 255, 0.1);
  border-left: 3px solid #00b4ff;
  background: linear-gradient(
    135deg,
    rgba(0, 180, 255, 0.07),
    rgba(124, 58, 237, 0.04)
  );
  font-size: 16px;
  line-height: 1.95;
  color: rgba(255, 255, 255, 0.8);
}

.body-text {
  margin: 0 0 16px;
  font-size: 15px;
  line-height: 1.9;
  color: rgba(255, 255, 255, 0.62);
}

.bullet-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin: 0 0 16px;
  padding: 0;
  list-style: none;
}

.bullet-list li {
  display: flex;
  gap: 10px;
  align-items: flex-start;
  font-size: 14px;
  line-height: 1.75;
  color: rgba(255, 255, 255, 0.6);
}

.bullet-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 20px;
  width: 20px;
  height: 20px;
  margin-top: 3px;
  border-radius: 50%;
  background: rgba(0, 180, 255, 0.12);
  color: #00b4ff;
}

.bullet-label {
  margin-right: 2px;
  color: #fff;
  font-weight: 600;
}

/* 平台数据概览 */
.stat-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 16px;
  margin: 8px 0 34px;
}

.stat-card {
  padding: 22px 18px;
  border-radius: 16px;
  border: 1px solid rgba(255, 255, 255, 0.06);
  background: rgba(255, 255, 255, 0.03);
  text-align: center;
  transition: all 0.3s;
}

.stat-card:hover {
  transform: translateY(-3px);
  border-color: rgba(0, 180, 255, 0.3);
}

.stat-icon {
  color: #00b4ff;
}

.stat-value {
  margin: 10px 0 4px;
  font-size: 26px;
  font-weight: 800;
  background: linear-gradient(135deg, #00b4ff, #7c3aed);
  -webkit-background-clip: text;
  background-clip: text;
  -webkit-text-fill-color: transparent;
}

.stat-label {
  font-size: 13px;
  color: rgba(255, 255, 255, 0.45);
}

/* 小标题 */
.sub-title {
  display: flex;
  align-items: center;
  gap: 10px;
  margin: 30px 0 16px;
  font-size: 19px;
  font-weight: 700;
  color: #fff;
}

.sub-title-icon {
  flex: 0 0 auto;
  color: #00b4ff;
}

/* 核心业务矩阵 */
.matrix-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 18px;
}

.matrix-card {
  padding: 22px;
  border-radius: 16px;
  border: 1px solid rgba(255, 255, 255, 0.06);
  background: rgba(255, 255, 255, 0.025);
  transition: all 0.3s;
}

.matrix-card:hover {
  border-color: rgba(0, 180, 255, 0.22);
  background: rgba(0, 180, 255, 0.04);
}

.matrix-head {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 12px;
}

.matrix-head h4 {
  margin: 0;
  font-size: 16px;
  font-weight: 700;
  color: #fff;
}

.matrix-icon {
  flex: 0 0 auto;
  color: #7c3aed;
}

.matrix-desc {
  margin: 0 0 12px;
  font-size: 14px;
  line-height: 1.8;
  color: rgba(255, 255, 255, 0.6);
}

.matrix-card .bullet-list {
  margin-bottom: 0;
}

/* 独特竞争优势 */
.advantage-list {
  display: flex;
  flex-direction: column;
  gap: 14px;
}

.advantage-item {
  display: flex;
  gap: 14px;
  padding: 16px 18px;
  border-radius: 14px;
  border: 1px solid rgba(255, 255, 255, 0.05);
  background: rgba(255, 255, 255, 0.02);
  transition: all 0.25s;
}

.advantage-item:hover {
  border-color: rgba(0, 180, 255, 0.18);
  background: rgba(255, 255, 255, 0.035);
}

.advantage-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 40px;
  width: 40px;
  height: 40px;
  border-radius: 12px;
  background: rgba(0, 180, 255, 0.1);
  color: #00b4ff;
}

.advantage-text {
  font-size: 14px;
  line-height: 1.8;
  color: rgba(255, 255, 255, 0.6);
}

.advantage-label {
  color: #fff;
  font-weight: 600;
}

/* 结语 */
.closing-quote {
  margin: 0;
  padding: 22px 24px;
  border-radius: 16px;
  border: 1px solid rgba(124, 58, 237, 0.22);
  border-left: 3px solid #7c3aed;
  background: linear-gradient(
    135deg,
    rgba(124, 58, 237, 0.12),
    rgba(0, 180, 255, 0.06)
  );
  font-size: 15px;
  line-height: 1.95;
  color: rgba(255, 255, 255, 0.78);
}

/* ==================== 2. 核心人员组织 ==================== */
.team-group {
  margin-bottom: 36px;
}

.group-title {
  display: flex;
  align-items: center;
  gap: 12px;
  margin: 0 0 16px;
  font-size: 17px;
  font-weight: 700;
  color: #fff;
}

.group-index {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 26px;
  height: 26px;
  border-radius: 8px;
  border: 1px solid rgba(0, 180, 255, 0.18);
  background: rgba(0, 180, 255, 0.1);
  font-size: 13px;
  font-weight: 700;
  color: #00b4ff;
}

.group-icon {
  flex: 0 0 auto;
  color: #7c3aed;
}

.member-cards.grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 18px;
}

.member-cards.wide {
  display: grid;
  grid-template-columns: 1fr;
  gap: 14px;
}

.member-card {
  display: flex;
  flex-direction: column;
  overflow: hidden;
  border-radius: 18px;
  border: 1px solid rgba(255, 255, 255, 0.06);
  background: rgba(255, 255, 255, 0.025);
  transition: all 0.3s;
}

.member-card:hover {
  transform: translateY(-3px);
  border-color: rgba(0, 180, 255, 0.28);
  box-shadow: 0 18px 40px -22px rgba(0, 180, 255, 0.55);
}

.member-photo-wrap {
  position: relative;
  width: 100%;
  aspect-ratio: 1 / 1;
  overflow: hidden;
  background: rgba(255, 255, 255, 0.04);
}

.member-photo {
  display: block;
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: center 22%;
  transition: transform 0.45s;
}

.member-card:hover .member-photo {
  transform: scale(1.05);
}

.member-info {
  flex: 1 1 auto;
  padding: 16px 18px 18px;
}

.member-name {
  margin: 0 0 6px;
  font-size: 16px;
  font-weight: 700;
  color: #fff;
}

.member-role {
  display: inline-block;
  padding: 3px 10px;
  margin-bottom: 10px;
  border-radius: 20px;
  border: 1px solid rgba(0, 180, 255, 0.16);
  background: rgba(0, 180, 255, 0.08);
  font-size: 12px;
  line-height: 1.6;
  color: #00b4ff;
}

.member-bio {
  margin: 0;
  font-size: 13px;
  line-height: 1.8;
  color: rgba(255, 255, 255, 0.56);
}

.bio-label {
  color: rgba(255, 255, 255, 0.78);
  font-weight: 600;
}

/* 单人分组：横向卡片 */
.member-card.wide {
  flex-direction: row;
  align-items: center;
  gap: 20px;
  padding: 18px;
}

.member-card.wide .member-photo-wrap {
  flex: 0 0 136px;
  width: 136px;
  height: 136px;
  aspect-ratio: auto;
  border-radius: 14px;
}

.member-card.wide .member-info {
  padding: 0;
}

/* 部门负责人分组：层级低于上方高管，整体更轻量、不加浮起动效 */
.team-group.compact {
  margin-bottom: 28px;
}

.team-group.compact .group-title {
  margin-bottom: 12px;
  font-size: 15px;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.78);
}

.team-group.compact .group-index {
  width: 22px;
  height: 22px;
  border-radius: 6px;
  border-color: rgba(255, 255, 255, 0.08);
  background: rgba(255, 255, 255, 0.04);
  font-size: 12px;
  color: rgba(255, 255, 255, 0.55);
}

.team-group.compact .group-icon {
  color: rgba(255, 255, 255, 0.42);
}

.member-cards.compact {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 12px;
}

.member-card.compact {
  flex-direction: row;
  align-items: flex-start;
  gap: 12px;
  padding: 14px;
  border-radius: 14px;
  border-color: rgba(255, 255, 255, 0.045);
  background: rgba(255, 255, 255, 0.015);
}

.member-card.compact:hover {
  transform: none;
  border-color: rgba(0, 180, 255, 0.18);
  box-shadow: none;
}

.member-card.compact .member-photo-wrap {
  flex: 0 0 100px;
  width: 100px;
  height: 140px;
  aspect-ratio: auto;
  border-radius: 12px;
}

.member-card.compact:hover .member-photo {
  transform: none;
}

.member-card.compact .member-info {
  flex: 1 1 auto;
  min-width: 0;
  padding: 0;
}

.member-card.compact .member-name {
  margin: 0 0 5px;
  font-size: 14px;
  font-weight: 600;
}

.member-card.compact .member-role {
  padding: 2px 8px;
  margin-bottom: 8px;
  border-color: rgba(255, 255, 255, 0.07);
  background: rgba(255, 255, 255, 0.05);
  font-size: 11px;
  color: rgba(255, 255, 255, 0.6);
}

.member-card.compact .member-bio {
  font-size: 12.5px;
  line-height: 1.7;
  color: rgba(255, 255, 255, 0.45);
}

.member-card.compact .bio-label {
  font-weight: 500;
  color: rgba(255, 255, 255, 0.62);
}

/* ==================== 3. 全球地址 ==================== */
.map-card {
  padding: 22px;
  border-radius: 18px;
  border: 1px solid rgba(255, 255, 255, 0.06);
  background: rgba(255, 255, 255, 0.025);
}

.map-head {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 18px;
}

.map-title {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 16px;
  font-weight: 700;
  color: #fff;
}

.map-title svg {
  flex: 0 0 auto;
  color: #00b4ff;
}

.map-hint {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  color: rgba(255, 255, 255, 0.4);
}

.map-hint svg {
  flex: 0 0 auto;
  color: #7c3aed;
}

.map-stage {
  position: relative;
  overflow: hidden;
  border-radius: 14px;
  border: 1px solid rgba(0, 180, 255, 0.08);
  background: radial-gradient(
      circle at 50% 45%,
      rgba(0, 180, 255, 0.09),
      transparent 62%
    ),
    linear-gradient(160deg, #0b1220, #0c1a30);
}

.world-map {
  display: block;
  width: 100%;
  height: auto;
}

.grid-layer line {
  stroke: rgba(0, 180, 255, 0.06);
  stroke-width: 1;
}

.land {
  fill: url(#aboutLandFill);
  stroke: rgba(0, 180, 255, 0.22);
  stroke-width: 1.1;
  stroke-linejoin: round;
}

.pin {
  cursor: pointer;
}

.pin:focus {
  outline: none;
}

.pin-dot {
  fill: #00b4ff;
  stroke: #041020;
  stroke-width: 2;
  transition: all 0.25s;
}

.pin-halo {
  fill: rgba(0, 180, 255, 0.16);
  transition: all 0.25s;
}

.pin-ring {
  fill: none;
  stroke: rgba(0, 180, 255, 0.42);
  stroke-width: 1.5;
  transform-box: fill-box;
  transform-origin: center;
  animation: pinPulse 2.6s ease-out infinite;
}

.pin-label {
  font-size: 15px;
  font-weight: 500;
  fill: rgba(255, 255, 255, 0.62);
  transition: fill 0.25s;
}

.pin.active .pin-dot,
.pin:hover .pin-dot,
.pin:focus .pin-dot {
  fill: #7c3aed;
}

.pin.active .pin-halo,
.pin:hover .pin-halo,
.pin:focus .pin-halo {
  fill: rgba(124, 58, 237, 0.28);
}

.pin.active .pin-ring,
.pin:hover .pin-ring,
.pin:focus .pin-ring {
  stroke: rgba(124, 58, 237, 0.6);
}

.pin.active .pin-label,
.pin:hover .pin-label,
.pin:focus .pin-label {
  fill: #fff;
}

@keyframes pinPulse {
  0% {
    transform: scale(0.55);
    opacity: 0.9;
  }
  70% {
    transform: scale(1.55);
    opacity: 0;
  }
  100% {
    transform: scale(1.55);
    opacity: 0;
  }
}

.map-active {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 12px;
  margin-top: 16px;
  padding: 14px 18px;
  border-radius: 12px;
  border: 1px solid rgba(0, 180, 255, 0.14);
  background: linear-gradient(
    135deg,
    rgba(0, 180, 255, 0.08),
    rgba(124, 58, 237, 0.05)
  );
}

.active-index {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 24px;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background: #00b4ff;
  color: #041020;
  font-size: 12px;
  font-weight: 700;
}

.active-country {
  font-size: 15px;
  font-weight: 700;
  color: #fff;
}

.active-addr {
  font-size: 13px;
  color: rgba(255, 255, 255, 0.55);
}

/* 悬停定位标记时就地弹出的地址气泡 */
.map-tip {
  position: absolute;
  z-index: 6;
  display: flex;
  align-items: flex-start;
  gap: 9px;
  max-width: 300px;
  padding: 11px 14px;
  border-radius: 12px;
  border: 1px solid rgba(0, 180, 255, 0.32);
  background: #0b1728;
  box-shadow: 0 14px 34px rgba(0, 0, 0, 0.5);
  pointer-events: none;
  animation: tipIn 0.18s ease-out;
}

@keyframes tipIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

.tip-arrow {
  position: absolute;
  width: 10px;
  height: 10px;
  background: #0b1728;
  border-right: 1px solid rgba(0, 180, 255, 0.32);
  border-bottom: 1px solid rgba(0, 180, 255, 0.32);
  transform: translateX(-50%) rotate(45deg);
}

.tip-top .tip-arrow {
  bottom: -6px;
}

.tip-bottom .tip-arrow {
  top: -6px;
  transform: translateX(-50%) rotate(225deg);
}

.tip-index {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 20px;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background: #00b4ff;
  color: #041020;
  font-size: 11px;
  font-weight: 700;
}

.tip-body {
  display: flex;
  flex-direction: column;
  gap: 3px;
}

.tip-country {
  font-size: 13px;
  font-weight: 700;
  line-height: 1.35;
  color: #fff;
}

.tip-addr {
  font-size: 12px;
  line-height: 1.6;
  color: rgba(255, 255, 255, 0.62);
  word-break: break-word;
}

/* ==================== 4. 资金安全 ==================== */
.fund-topic {
  margin-bottom: 34px;
}

.fund-topic + .fund-topic {
  margin-top: 40px;
}

.fund-topic:last-child {
  margin-bottom: 0;
}

/* 每个主题的首个小标题无需额外上间距 */
.fund-topic .sub-title {
  margin-top: 0;
}

/* 权威强援：第三方信托机构独立托管背书 */
.fund-custody {
  margin: 0 0 18px;
  padding: 20px 22px;
  border-radius: 16px;
  border: 1px solid rgba(0, 180, 255, 0.16);
  border-left: 3px solid rgba(0, 180, 255, 0.75);
  background: linear-gradient(
    135deg,
    rgba(0, 180, 255, 0.1),
    rgba(124, 58, 237, 0.05)
  );
  box-shadow: 0 18px 36px -32px rgba(0, 180, 255, 0.9);
}

.fund-custody-title {
  margin: 0 0 10px;
  font-size: 16px;
  font-weight: 700;
  line-height: 1.6;
  color: #fff;
}

.fund-custody-intro {
  margin: 0 0 14px;
  font-size: 14px;
  line-height: 1.9;
  color: rgba(255, 255, 255, 0.7);
}

.fund-custody .bullet-list {
  margin-bottom: 0;
}

.fund-custody .bullet-text {
  color: rgba(255, 255, 255, 0.68);
}

/* 整行图示：纯 HTML/CSS 绘制，宽度随内容自适应 */
.fund-diagram {
  margin: 0 0 18px;
  min-width: 0;
}

/* 平台防护体系：三大核心机制 */
.fund-detail-list {
  display: grid;
  gap: 14px;
  margin: 0 0 16px;
}

.fund-detail-card {
  padding: 18px 20px;
  border-radius: 14px;
  border: 1px solid rgba(255, 255, 255, 0.06);
  border-left: 3px solid rgba(0, 180, 255, 0.55);
  background: linear-gradient(
    135deg,
    rgba(0, 180, 255, 0.06),
    rgba(124, 58, 237, 0.035)
  );
  transition: all 0.25s;
}

.fund-detail-card:hover {
  border-color: rgba(0, 180, 255, 0.22);
  background: rgba(0, 180, 255, 0.05);
}

.fund-detail-title {
  margin: 0 0 8px;
  font-size: 15px;
  font-weight: 700;
  line-height: 1.6;
  color: #fff;
}

.fund-detail-text {
  margin: 0;
  font-size: 14px;
  line-height: 1.85;
  color: rgba(255, 255, 255, 0.62);
}

/* ==================== 响应式 ==================== */
@media (max-width: 1200px) {
  .about-container {
    gap: 32px;
  }

  .about-sidebar {
    flex-basis: 224px;
  }
}

@media (max-width: 1024px) {
  .about-container {
    flex-direction: column;
    gap: 24px;
  }

  .about-sidebar {
    position: static;
    flex: 1 1 auto;
    width: 100%;
    max-height: none;
  }

  .sidebar-nav {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 8px;
  }

  .sidebar-link {
    justify-content: center;
    background: rgba(255, 255, 255, 0.03);
  }

  .member-cards.grid,
  .member-cards.compact {
    grid-template-columns: repeat(2, 1fr);
  }
}

@media (max-width: 768px) {
  .about-hero {
    padding: 52px 18px 40px;
  }

  .hero-title {
    font-size: 32px;
  }

  .about-body {
    padding: 28px 16px 60px;
  }

  .section-title-text {
    font-size: 24px;
  }

  .section-badge {
    font-size: 12px;
  }

  .stat-grid,
  .matrix-grid {
    grid-template-columns: 1fr;
  }

  .sidebar-nav {
    grid-template-columns: 1fr;
  }

  .sidebar-link {
    justify-content: flex-start;
  }

  .member-cards.grid,
  .member-cards.compact {
    grid-template-columns: 1fr;
  }

  .member-card.wide {
    flex-direction: column;
    align-items: stretch;
    gap: 14px;
    padding: 16px;
  }

  .member-card.wide .member-photo-wrap {
    flex: 0 0 auto;
    width: 100%;
    height: auto;
    aspect-ratio: 16 / 9;
  }

  .map-card {
    padding: 16px;
  }

  .map-hint {
    font-size: 11px;
  }

  .pin-label {
    font-size: 17px;
  }

  .map-tip {
    max-width: 240px;
    padding: 9px 11px;
  }

  .tip-country {
    font-size: 12px;
  }

  .tip-addr {
    font-size: 11px;
  }
}

@media (max-width: 480px) {
  .hero-title {
    font-size: 26px;
  }

  .hero-sub {
    font-size: 14px;
  }

  .stat-value {
    font-size: 22px;
  }

  .map-active {
    flex-direction: column;
    align-items: flex-start;
    gap: 6px;
  }

  .pin-label {
    font-size: 19px;
  }
}

/* 触屏设备无悬停能力：改用点击定位标记 → 底部地址条展示 */
@media (hover: none) {
  .map-tip {
    display: none;
  }
}

/* 尊重系统「减少动态效果」设置：关闭面板切换与气泡动画 */
@media (prefers-reduced-motion: reduce) {
  .about-section,
  .map-tip {
    animation: none;
  }
}
</style>
